/* ==========================================================================
   Celestial Grace — theme.js
   Cart drawer, quick add, sliders, predictive search, variant picker
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.themeRoutes || {};
  var strings = window.themeStrings || {};
  var settings = window.themeSettings || {};

  function formatMoney(cents) {
    var format = settings.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) { throw data; }
        return data;
      });
    });
  }

  /* ---------- Section re-rendering helpers ---------- */

  function parseSectionHTML(html, sectionId) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return sectionId ? doc.querySelector('#shopify-section-' + sectionId) : doc;
  }

  /* ---------- Cart ---------- */

  var Cart = {
    drawer: null,
    panel: null,

    init: function () {
      this.drawer = document.querySelector('cart-drawer');
      if (this.drawer) { this.panel = this.drawer.querySelector('.cart-drawer__panel'); }
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest('[data-cart-open]');
        if (trigger) {
          e.preventDefault();
          Cart.open();
          return;
        }
        var closer = e.target.closest('[data-cart-close]');
        if (closer) { Cart.close(); }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { Cart.close(); }
      });

      // Quantity + remove (works in drawer and cart page)
      document.addEventListener('click', function (e) {
        var qtyBtn = e.target.closest('[data-qty]');
        if (qtyBtn) {
          var wrap = qtyBtn.closest('.quantity');
          var input = wrap && wrap.querySelector('.quantity__input');
          if (!input) { return; }
          var current = parseInt(input.value, 10) || 1;
          var next = qtyBtn.getAttribute('data-qty') === 'plus' ? current + 1 : current - 1;
          Cart.changeLine(input.getAttribute('data-line'), next, qtyBtn.closest('[data-cart-item]'));
        }
        var removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          Cart.changeLine(removeBtn.getAttribute('data-line'), 0, removeBtn.closest('[data-cart-item]'));
        }
      });

      // Note toggles
      document.addEventListener('click', function (e) {
        var noteBtn = e.target.closest('[data-cart-note-toggle]');
        if (noteBtn) {
          var note = document.querySelector(noteBtn.getAttribute('data-cart-note-toggle'));
          if (note) { note.classList.toggle('is-visible'); }
        }
      });

      // Note save (delegated: survives drawer re-render)
      document.addEventListener('change', function (e) {
        var noteField = e.target.closest('#cart-drawer-note textarea, #cart-note');
        if (noteField) {
          fetchJSON(routes.cart_update_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: noteField.value })
          }).catch(function () {});
        }
      });

      // WhatsApp & email updates opt-in (saved as a cart attribute)
      document.addEventListener('change', function (e) {
        var optin = e.target.closest('input[name="attributes[WhatsApp updates opt-in]"]');
        if (!optin) { return; }
        var attrs = {};
        attrs['WhatsApp updates opt-in'] = optin.checked ? 'Yes' : 'No';
        fetchJSON(routes.cart_update_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).catch(function () {});
      });
    },

    open: function () {
      if (!this.drawer) { window.location.href = routes.cart_url; return; }
      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var closeBtn = this.drawer.querySelector('[data-cart-close]');
      if (closeBtn) { closeBtn.focus(); }
    },

    close: function () {
      if (this.drawer) { this.drawer.classList.remove('is-open'); }
      document.body.style.overflow = '';
    },

    get: function () {
      return fetchJSON(routes.cart_url + '.js', { headers: { 'Content-Type': 'application/json' } });
    },

    changeLine: function (line, quantity, itemEl) {
      if (line === null || line === undefined) { return; }
      if (itemEl) { itemEl.classList.add('is-updating'); }
      fetchJSON(routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
      })
        .then(function (cart) { Cart.refresh(cart); })
        .catch(function () { if (itemEl) { itemEl.classList.remove('is-updating'); } });
    },

    add: function (formData) {
      return fetchJSON(routes.cart_add_url + '.js', { method: 'POST', body: formData }).then(function (item) {
        return Cart.get().then(function (cart) {
          Cart.refresh(cart);
          return item;
        });
      });
    },

    refresh: function (cart) {
      Cart.updateCount(cart);
      Cart.updateShippingBar(cart);
      Cart.reloadPanel();
    },

    updateCount: function (cart) {
      var total = cart.item_count;
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = total;
        el.hidden = total === 0;
      });
    },

    updateShippingBar: function (cart) {
      document.querySelectorAll('[data-shipping-bar]').forEach(function (bar) {
        if (!settings.cartShowShippingBar) { bar.hidden = true; return; }
        var threshold = settings.freeShippingThreshold || 0;
        if (!threshold) { bar.hidden = true; return; }
        var total = cart.items.reduce(function (sum, item) {
          return sum + item.final_line_price;
        }, 0);
        var remaining = Math.max(threshold - total, 0);
        var pct = Math.min((total / threshold) * 100, 100);
        var msgEl = bar.querySelector('[data-shipping-message]');
        var fillEl = bar.querySelector('.shipping-bar__fill');
        if (fillEl) { fillEl.style.width = pct + '%'; }
        if (msgEl) {
          msgEl.textContent = remaining > 0
            ? (strings.freeShippingRemaining || '').replace('[amount]', formatMoney(remaining))
            : (strings.freeShippingUnlocked || 'You have unlocked free shipping!');
        }
        bar.classList.toggle('shipping-bar--unlocked', remaining === 0);
      });
    },

    reloadPanel: function () {
      var drawer = document.querySelector('cart-drawer');
      var sectionId = drawer ? drawer.getAttribute('data-section-id') : 'cart-drawer';
      fetch(window.location.pathname + '?sections=' + sectionId)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var html = data[sectionId];
          if (!html || !drawer) { return; }
          var fresh = parseSectionHTML(html, sectionId);
          var freshDrawer = fresh && (fresh.querySelector('cart-drawer') || (fresh.matches && fresh.matches('cart-drawer') ? fresh : null));
          if (!freshDrawer) { return; }
          drawer.innerHTML = freshDrawer.innerHTML;
        })
        .catch(function () {});
    }
  };

  /* ---------- Quick add ---------- */

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-quick-add]');
    if (!form) { return; }
    e.preventDefault();
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('is-loading');
    }
    var errorEl = form.parentElement && form.parentElement.querySelector('.quick-add__error');
    if (errorEl) { errorEl.hidden = true; }
    Cart.add(new FormData(form))
      .then(function () { Cart.open(); })
      .catch(function (err) {
        if (errorEl) {
          errorEl.textContent = (err && err.description) || strings.cartError || 'Could not add to cart.';
          errorEl.hidden = false;
        }
      })
      .finally(function () {
        if (btn) {
          btn.removeAttribute('disabled');
          btn.classList.remove('is-loading');
        }
      });
  });

  /* ---------- Product form (variant selection + add to cart) ---------- */

  function initProductForms(root) {
    (root || document).querySelectorAll('product-form').forEach(function (wrapper) {
      if (wrapper.dataset.initialised) { return; }
      wrapper.dataset.initialised = 'true';

      var dataEl = wrapper.querySelector('[data-product-json]');
      var form = wrapper.querySelector('form');
      if (!dataEl || !form) { return; }

      var product = null;
      try { product = JSON.parse(dataEl.textContent); } catch (err) { return; }

      var idInput = form.querySelector('[name="id"]');
      var swatches = wrapper.querySelectorAll('[data-option-select]');
      var errorEl = wrapper.querySelector('.product-form__error, [data-form-error]');
      var submitBtn = form.querySelector('[type="submit"]');
      var priceEls = document.querySelectorAll('[data-product-price]');

      function selectedOptions() {
        var values = [];
        swatches.forEach(function (group) {
          var sel = group.querySelector('.is-selected');
          values.push(sel ? sel.getAttribute('data-value') : null);
        });
        return values;
      }

      function matchingVariant(values) {
        return product.variants.find(function (v) {
          return v.options.every(function (opt, i) { return opt === values[i]; });
        });
      }

      function variantAvailable(variant) {
        if (!variant) { return false; }
        if (typeof variant.available === 'boolean') { return variant.available; }
        return true;
      }

      function update() {
        var values = selectedOptions();
        // Availability hints per swatch
        swatches.forEach(function (group, gi) {
          group.querySelectorAll('.product-form__swatch').forEach(function (swatch) {
            var test = values.slice();
            test[gi] = swatch.getAttribute('data-value');
            var match = matchingVariant(test);
            swatch.classList.toggle('is-unavailable', !variantAvailable(match));
          });
        });
        var variant = matchingVariant(values);
        swatches.forEach(function (group) {
          var label = group.querySelector('[data-selected-value]');
          var sel = group.querySelector('.is-selected');
          if (label && sel) { label.textContent = sel.getAttribute('data-value'); }
        });
        if (idInput && variant) { idInput.value = variant.id; }
        if (variant && priceEls.length) {
          var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          priceEls.forEach(function (el) {
            var reg = el.querySelector('[data-price-regular]');
            var compare = el.querySelector('[data-price-compare]');
            if (reg) { reg.textContent = formatMoney(variant.price); }
            if (compare) {
              compare.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';
              compare.hidden = !onSale;
            }
            el.classList.toggle('price--on-sale', !!onSale);
          });
        }
        if (submitBtn) {
          if (!variant) {
            submitBtn.textContent = strings.unavailable || 'Unavailable';
            submitBtn.setAttribute('disabled', 'disabled');
          } else if (!variantAvailable(variant)) {
            submitBtn.textContent = strings.soldOut || 'Sold out';
            submitBtn.setAttribute('disabled', 'disabled');
          } else {
            submitBtn.textContent = strings.addToCart || 'Add to cart';
            submitBtn.removeAttribute('disabled');
          }
        }
        if (variant && window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      wrapper.addEventListener('click', function (e) {
        var swatch = e.target.closest('.product-form__swatch');
        if (!swatch) { return; }
        var group = swatch.closest('[data-option-select]');
        group.querySelectorAll('.product-form__swatch').forEach(function (s) {
          s.classList.toggle('is-selected', s === swatch);
        });
        update();
      });

      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          if (submitBtn) {
            submitBtn.setAttribute('disabled', 'disabled');
            submitBtn.textContent = strings.adding || 'Adding…';
          }
          if (errorEl) { errorEl.classList.remove('is-visible'); }
          Cart.add(new FormData(form))
            .then(function () {
              Cart.open();
              if (submitBtn) { submitBtn.textContent = strings.added || 'Added!'; }
              setTimeout(function () { update(); }, 1200);
            })
            .catch(function (err) {
              if (errorEl) {
                errorEl.textContent = (err && err.description) || strings.cartError || 'Could not add to cart.';
                errorEl.classList.add('is-visible');
              }
              if (submitBtn) { update(); }
            });
        });
      }

      update();
    });
  }

  /* ---------- Sliders (announcement bar, hero, product/testimonial sliders) ---------- */

  function initSliders(root) {
    (root || document).querySelectorAll('[data-slider]').forEach(function (slider) {
      if (slider.dataset.sliderInit) { return; }
      slider.dataset.sliderInit = 'true';
      var viewport = slider.querySelector('[data-slider-viewport]');
      if (!viewport) { return; }

      var prev = slider.querySelector('[data-slider-prev]');
      var next = slider.querySelector('[data-slider-next]');

      function step() {
        var child = viewport.querySelector(':scope > *');
        if (!child) { return 240; }
        return child.getBoundingClientRect().width + parseFloat(getComputedStyle(viewport).columnGap || getComputedStyle(viewport).gap || 20);
      }
      function updateArrows() {
        if (!prev && !next) { return; }
        var max = viewport.scrollWidth - viewport.clientWidth - 4;
        if (prev) { prev.toggleAttribute('disabled', viewport.scrollLeft <= 4); }
        if (next) { next.toggleAttribute('disabled', viewport.scrollLeft >= max); }
      }
      if (prev) {
        prev.addEventListener('click', function () { viewport.scrollBy({ left: -step(), behavior: 'smooth' }); });
      }
      if (next) {
        next.addEventListener('click', function () { viewport.scrollBy({ left: step(), behavior: 'smooth' }); });
      }
      function moveSlide(dir) {
        viewport.scrollBy({ left: dir * step(), behavior: 'smooth' });
      }
      slider.shopifyPrev = function () { moveSlide(-1); };
      slider.shopifyNext = function () { moveSlide(1); };
      viewport.addEventListener('scroll', debounce(updateArrows, 80), { passive: true });
      window.addEventListener('resize', debounce(updateArrows, 150));
      updateArrows();
    });
  }

  function initHero(root) {
    (root || document).querySelectorAll('[data-hero]').forEach(function (hero) {
      if (hero.dataset.heroInit) { return; }
      hero.dataset.heroInit = 'true';
      var viewport = hero.querySelector('[data-hero-viewport]');
      var slides = viewport ? Array.prototype.slice.call(viewport.children) : [];
      var dots = Array.prototype.slice.call(hero.querySelectorAll('[data-hero-dot]'));
      var index = 0;
      var timer = null;
      var interval = parseInt(hero.getAttribute('data-autoplay'), 10) || 0;

      function go(i) {
        index = (i + slides.length) % slides.length;
        if (viewport) { viewport.style.transform = 'translateX(-' + index * 100 + '%)'; }
        dots.forEach(function (dot, di) { dot.classList.toggle('is-active', di === index); });
      }
      function play() {
        if (interval > 0 && slides.length > 1) {
          stop();
          timer = setInterval(function () { go(index + 1); }, interval);
        }
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      dots.forEach(function (dot, di) {
        dot.addEventListener('click', function () { go(di); play(); });
      });
      var prev = hero.querySelector('[data-hero-prev]');
      var next = hero.querySelector('[data-hero-next]');
      if (prev) { prev.addEventListener('click', function () { go(index - 1); play(); }); }
      if (next) { next.addEventListener('click', function () { go(index + 1); play(); }); }
      hero.addEventListener('mouseenter', stop);
      hero.addEventListener('mouseleave', play);
      hero.shopifyPrev = function () { go(index - 1); play(); };
      hero.shopifyNext = function () { go(index + 1); play(); };
      go(0);
      play();
    });
  }

  function initAnnouncementBar(root) {
    (root || document).querySelectorAll('[data-announcement]').forEach(function (bar) {
      if (bar.dataset.annInit) { return; }
      bar.dataset.annInit = 'true';
      var viewport = bar.querySelector('.announcement-bar__viewport');
      var items = viewport ? Array.prototype.slice.call(viewport.children) : [];
      if (items.length < 2) { return; }
      var interval = parseInt(bar.getAttribute('data-speed'), 10) || 5000;
      var index = 0;
      setInterval(function () {
        index = (index + 1) % items.length;
        viewport.style.transform = 'translateX(-' + index * 100 + '%)';
      }, interval);
    });
  }

  /* ---------- Header behaviour ---------- */

  function initHeader() {
    var wrapper = document.querySelector('.header-wrapper');
    if (wrapper) {
      var onScroll = function () {
        wrapper.classList.toggle('is-scrolled', window.scrollY > 4);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }    // Mobile menu
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-menu-open]');
      if (openBtn) {
        e.preventDefault();
        var menu = document.querySelector('.mobile-menu');
        if (menu) {
          menu.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        }
      }
      var closeBtn = e.target.closest('[data-menu-close]');
      if (closeBtn) {
        var m = document.querySelector('.mobile-menu');
        if (m) {
          m.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      }
    });

    // Search modal
    var searchModal = document.querySelector('.header-search');
    document.addEventListener('click', function (e) {
      var openSearch = e.target.closest('[data-search-open]');
      if (openSearch && searchModal) {
        e.preventDefault();
        searchModal.classList.add('is-open');
        var input = searchModal.querySelector('input[type="search"]');
        if (input) { input.focus(); }
      }
      var closeSearch = e.target.closest('[data-search-close]');
      if (closeSearch && searchModal) {
        searchModal.classList.remove('is-open');
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && searchModal) { searchModal.classList.remove('is-open'); }
    });
  }

  /* ---------- Predictive search ---------- */

  function initPredictiveSearch() {
    var modal = document.querySelector('.header-search');
    if (!modal || !settings.predictiveSearchEnabled) { return; }
    var input = modal.querySelector('input[type="search"]');
    var results = modal.querySelector('[data-predictive-results]');
    var status = modal.querySelector('[data-predictive-status]');
    if (!input || !results) { return; }

    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        if (status) { status.textContent = ''; }
        return;
      }
      var resources = 'product';
      if (settings.searchShowPages !== false) { resources += ',page,article,collection'; }
      fetch(routes.predictive_search_url + '?q=' + encodeURIComponent(q) + '&resources[type]=' + resources + '&resources[limit]=8&section_id=predictive-search')
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = parseSectionHTML(html, 'predictive-search');
          results.innerHTML = doc ? doc.innerHTML : '';
          if (status) { status.textContent = ''; }
        })
        .catch(function () {
          if (status) { status.textContent = strings.cartError || ''; }
        });
    }, 250);

    input.addEventListener('input', function () {
      if (status) { status.textContent = '…'; }
      run();
    });
  }

  /* ---------- FAQ: one open at a time ---------- */

  function initFaq() {
    // Event-delegated so it also works for content injected after boot (e.g. preview pages)
    document.addEventListener('toggle', function (e) {
      var faq = e.target.closest('[data-faq-single]');
      if (!faq || !e.target.open) { return; }
      faq.querySelectorAll('details[open]').forEach(function (d) {
        if (d !== e.target) { d.open = false; }
      });
    }, true);
  }

  /* ---------- Sticky-ish gallery scroll spy ---------- */

  function initGallery() {
    var gallery = document.querySelector('.product-gallery');
    if (!gallery) { return; }
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('.product-gallery__thumb'));
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var target = document.getElementById(thumb.getAttribute('data-thumb-target'));
        if (target) { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
        thumbs.forEach(function (t) { t.classList.toggle('is-active', t === thumb); });
      });
    });
  }

  /* ---------- Welcome popup ---------- */

  function initPopup() {
    var popup = document.querySelector('[data-popup]');
    if (!popup || popup.dataset.popupInit) { return; }
    popup.dataset.popupInit = 'true';

    function close(store) {
      popup.classList.remove('is-open');
      document.body.style.overflow = '';
      if (store && popup.getAttribute('data-popup-once') === 'true') {
        try { localStorage.setItem('popup-dismissed-' + popup.getAttribute('data-popup-id'), Date.now()); } catch (e) {}
      }
    }

    popup.querySelectorAll('[data-popup-close]').forEach(function (el) {
      el.addEventListener('click', function () { close(true); });
    });

    // Tap-to-copy discount code chip
    popup.querySelectorAll('[data-copy-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var code = btn.getAttribute('data-code') || '';
        var hint = btn.querySelector('[data-copy-hint]');
        var original = hint ? hint.textContent : '';
        function done(ok) {
          if (hint) {
            hint.textContent = ok ? 'Copied!' : 'Press Ctrl+C';
            btn.classList.add('is-copied');
            setTimeout(function () {
              hint.textContent = original;
              btn.classList.remove('is-copied');
            }, 2000);
          }
        }
        function legacyCopy() {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          var ok = false;
          try { ok = document.execCommand('copy'); } catch (e) {}
          document.body.removeChild(ta);
          return ok;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(function () { done(true); }, function () { done(legacyCopy()); });
        } else {
          done(legacyCopy());
        }
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(true); }
    });

    var delay = parseInt(popup.getAttribute('data-popup-delay'), 10);
    if (isNaN(delay)) { delay = 1; }

    var dismissed = false;
    try { dismissed = !!localStorage.getItem('popup-dismissed-' + popup.getAttribute('data-popup-id')); } catch (e) {}
    var showOnce = popup.getAttribute('data-popup-once') === 'true';

    // In the theme editor, section reloads keep localStorage — clear the flag so
    // merchants always see the popup while editing it.
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target.contains && e.target.contains(popup)) {
        try { localStorage.removeItem('popup-dismissed-' + popup.getAttribute('data-popup-id')); } catch (err) {}
        dismissed = false;
      }
    });

    if (dismissed && showOnce) { return; }

    setTimeout(function () {
      popup.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }, delay * 1000);
  }

  /* ---------- Estimated delivery date (product page) ---------- */

  function initDeliveryEstimate() {
    document.querySelectorAll('[data-delivery-estimate]').forEach(function (el) {
      if (el.dataset.deliveryInit) { return; }
      el.dataset.deliveryInit = 'true';
      var dispatchDays = parseInt(el.getAttribute('data-dispatch-days'), 10) || 1;
      var minDays = parseInt(el.getAttribute('data-min'), 10) || 2;
      var maxDays = parseInt(el.getAttribute('data-max'), 10) || 6;

      function addBusinessDays(from, days) {
        var d = new Date(from);
        var added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          var day = d.getDay();
          if (day !== 0) { added++; } /* Sundays are non-working */
        }
        return d;
      }

      function render() {
        var dispatch = addBusinessDays(new Date(), dispatchDays);
        var from = addBusinessDays(dispatch, minDays);
        var to = addBusinessDays(dispatch, maxDays);
        var opts = { weekday: 'short', day: 'numeric', month: 'short' };
        el.textContent = from.toLocaleDateString('en-IN', opts) + ' – ' + to.toLocaleDateString('en-IN', opts);
      }

      render();
    });
  }

  /* ---------- Share button (product page) ---------- */

  function initShareButton() {
    document.querySelectorAll('[data-share-button]').forEach(function (btn) {
      if (btn.dataset.shareInit) { return; }
      btn.dataset.shareInit = 'true';
      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-share-url') || window.location.href;
        var title = btn.getAttribute('data-share-title') || document.title;
        var label = btn.querySelector('[data-share-label]');
        var originalText = label ? label.textContent : '';
        function confirmCopied() {
          if (!label) { return; }
          label.textContent = 'Link copied!';
          btn.classList.add('is-copied');
          setTimeout(function () {
            label.textContent = originalText;
            btn.classList.remove('is-copied');
          }, 2000);
        }
        function copyFallback() {
          var input = document.createElement('input');
          input.value = url;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          try { document.execCommand('copy'); confirmCopied(); } catch (e) {}
          document.body.removeChild(input);
        }
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(confirmCopied).catch(copyFallback);
        } else {
          copyFallback();
        }
      });
    });
  }

  /* ---------- Watch and Shop: hover/tap to play ---------- */

  function initWatchShop() {
    document.addEventListener('mouseover', function (e) {
      var media = e.target.closest('[data-hover-video]');
      if (!media) { return; }
      var v = media.querySelector('video');
      if (v) { v.play().catch(function () {}); }
    });
    document.addEventListener('mouseout', function (e) {
      var media = e.target.closest('[data-hover-video]');
      if (!media) { return; }
      var v = media.querySelector('video');
      if (v && !v.paused) { v.pause(); }
    });
    document.addEventListener('click', function (e) {
      var media = e.target.closest('[data-hover-video]');
      if (!media) { return; }
      var v = media.querySelector('video');
      if (!v) { return; }
      e.preventDefault();
      if (v.paused) { v.play().catch(function () {}); } else { v.pause(); }
    });
  }

  /* ---------- Boot ---------- */

  function init(root) {
    Cart.init();
    initProductForms(root);
    initSliders(root);
    initHero(root);
    initAnnouncementBar(root);
    initHeader();
    initPredictiveSearch();
    initFaq(root);
    initGallery();
    initPopup();
    initDeliveryEstimate();
    initWatchShop();
    initShareButton();
  }

  /* ---------- Logo loading animation settle ---------- */

  function initLogoLoading() {
    var root = document.documentElement;
    if (!root.classList.contains('is-loading')) { return; }
    var done = false;
    function settle() {
      if (done) { return; }
      done = true;
      root.classList.remove('is-loading');
    }
    function imagesReady() {
      var imgs = document.images;
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].complete) { return false; }
      }
      return true;
    }
    /* Cap the spin at 3.5s so a slow image never leaves the logo spinning forever */
    setTimeout(settle, 3500);
    if (document.readyState === 'complete') {
      settle();
    } else {
      window.addEventListener('load', settle);
      /* data-URIs and cached images may finish before 'load' fires */
      var poll = setInterval(function () {
        if (imagesReady()) { clearInterval(poll); settle(); }
      }, 250);
      setTimeout(function () { clearInterval(poll); }, 3600);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); initLogoLoading(); });
  } else {
    init(document);
    initLogoLoading();
  }

  // Re-init after theme editor section reloads
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
